import { useState, useEffect, useCallback } from 'react';

import { getPendingTeachers, approveTeacher, rejectTeacher } from '../api/admin.api';

export default function usePendingTeachers(page = 1, perPage = 15, filters = {}) {
  const { search = '', subject = '', date = '' } = filters;
  const [teachers, setTeachers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState(null);

  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getPendingTeachers(page, perPage, { search, subject, date });

      if (res.data) {
        setTeachers(res.data.data);
        setMeta(res.data.meta);
      }
    } catch (err) {
      setError('Failed to load pending teachers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, subject, date]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const approve = async (id) => {
    setActionLoading(id);
    try {
      await approveTeacher(id);
      setTeachers((prev) => prev.filter((t) => t.id !== id));
      setMeta((prev) => (prev ? { ...prev, total: prev.total - 1 } : prev));
      return { success: true, message: 'Teacher approved successfully' };
    } catch (err) {
      return { success: false, message: 'Failed to approve teacher' };
    } finally {
      setActionLoading(null);
    }
  };

  const reject = async (id) => {
    setActionLoading(id);
    try {
      await rejectTeacher(id);
      setTeachers((prev) => prev.filter((t) => t.id !== id));
      setMeta((prev) => (prev ? { ...prev, total: prev.total - 1 } : prev));
      return { success: true, message: 'Teacher rejected successfully' };
    } catch (err) {
      return { success: false, message: 'Failed to reject teacher' };
    } finally {
      setActionLoading(null);
    }
  };

  return {
    teachers,
    meta,
    loading,
    actionLoading,
    error,
    approve,
    reject,
    refetch: fetchTeachers,
  };
}
